import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Tag, Search, ChevronDown } from 'lucide-react';
import usePopoverPosition from '../../hooks/usePopoverPosition';
import OsScroll from '../ui/OsScroll';
import { pillColors } from '../../extensions/bundled/task-manager/boardLabels';

/**
 * The board filter's labels as ONE dropdown with checkboxes (owner,
 * 13.10.2026): when the label chips do not all fit on the toolbar row they
 * are replaced by this "Labels" chip — every label a checkbox row with its
 * dot and task count, several at once (ANY of them, like the chips), the menu
 * stays open while ticking. Built from the app picker's parts like
 * TaskLabelPicker; the trigger wears the Priority chip's look.
 *
 * @param {{ palette: {id,name,color}[], counts: Object<string, number>, selected: string[], onChange: (ids: string[]) => void }} props
 */
const menuWidth = (r) => Math.max(r.width, 220);
const SEARCH_FROM = 9;

export default function BoardLabelsFilter({ palette = [], counts = {}, selected = [], onChange, standby = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const triggerRef = useRef(null);
  const searchRef = useRef(null);
  const pos = usePopoverPosition({ anchorRef: triggerRef, open, width: menuWidth, placement: 'auto', deps: [palette.length, selected.length] });
  const on = selected.length > 0;

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
  }, []);
  useEffect(() => {
    if (!open) return undefined;
    searchRef.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, close]);

  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const q = query.trim().toLowerCase();
  const shown = q ? palette.filter((l) => l.name.toLowerCase().includes(q)) : palette;

  const menu = open
    ? createPortal(
        <>
          <div
            className="auth-picker-backdrop"
            aria-hidden
            onMouseDown={(e) => {
              e.preventDefault();
              close();
            }}
          />
          <div
            className="auth-picker__menu board-filter-priority-menu board-filter-labels-menu"
            role="listbox"
            aria-multiselectable="true"
            aria-label="Filter by labels"
            style={{
              position: 'fixed',
              ...(pos.dir === 'up' ? { bottom: pos.bottom } : { top: pos.top }),
              left: pos.left,
              minWidth: pos.width,
              maxHeight: pos.maxHeight,
              zIndex: 10040,
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {palette.length >= SEARCH_FROM && (
              <div className="auth-picker__search">
                <Search size={12} className="auth-picker__search-icon" aria-hidden />
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Find a label…"
                  className="auth-picker__search-input"
                  aria-label="Find a label"
                />
              </div>
            )}
            <OsScroll style={{ maxHeight: '16rem', minHeight: 0 }}>
              <div className="auth-picker__list auth-picker__list--os">
                {shown.map((l) => {
                  const checked = selected.includes(l.id);
                  return (
                    <label key={l.id} className={`auth-picker__row board-filter-labels-row${checked ? ' auth-picker__row--active' : ''}`} role="option" aria-selected={checked}>
                      <input type="checkbox" className="checkbox-brand" checked={checked} onChange={() => toggle(l.id)} aria-label={l.name} />
                      <span className="board-filter-chip-dot" style={{ background: pillColors(l).dot }} aria-hidden />
                      <span className="min-w-0 flex-1 truncate text-left">{l.name}</span>
                      <span className="board-filter-priority-count">{counts[l.id] || 0}</span>
                    </label>
                  );
                })}
                {!shown.length && <div className="auth-picker__empty">No matching label</div>}
              </div>
            </OsScroll>
            {on && (
              <div className="auth-picker__footer">
                <button type="button" className="board-filter-priority-any" onClick={() => onChange([])}>
                  Clear labels
                </button>
              </div>
            )}
          </div>
        </>,
        document.body
      )
    : null;

  return (
    <>
      <div className={`auth-picker board-filter-priority board-filter-more board-filter-labels${standby ? ' board-filter-labels--standby' : ''}${on ? ' board-filter-priority--on' : ''}`} ref={triggerRef}>
        <button
          type="button"
          className="auth-picker__trigger"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={on ? `Labels filter: ${selected.length} selected` : 'Filter by labels'}
          onClick={() => (open ? close() : setOpen(true))}
        >
          <Tag size={12} aria-hidden />
          <span className="auth-picker__trigger-label">{on ? `Labels · ${selected.length}` : 'Labels'}</span>
          <ChevronDown size={14} className={`auth-picker__chevron ${open ? 'auth-picker__chevron--open' : ''}`} aria-hidden />
        </button>
      </div>
      {menu}
    </>
  );
}
